import { useEffect, useState } from "react";
import api from "@/lib/api";
import { STAGES, CASE_TYPES, fmtDate } from "@/lib/utils-domain";
import { Plus, X, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

const STAGE_META = {
    Leads: { color: "border-t-slate-400", chip: "bg-slate-100 text-slate-700" },
    Consultation: { color: "border-t-blue-500", chip: "bg-blue-100 text-blue-700" },
    Retained: { color: "border-t-emerald-500", chip: "bg-emerald-100 text-emerald-700" },
};

export default function CRM() {
    const [leads, setLeads] = useState([]);
    const [open, setOpen] = useState(false);

    const load = async () => setLeads((await api.get("/leads")).data);
    useEffect(() => { load(); }, []);

    const moveLead = async (lead_id, stage) => {
        await api.patch(`/leads/${lead_id}`, { stage });
        load();
    };
    const convert = async (lead_id) => {
        await api.post(`/leads/${lead_id}/convert`);
        toast.success("Lead converted to case");
        load();
    };
    const remove = async (lead_id) => {
        await api.delete(`/leads/${lead_id}`);
        load();
    };

    return (
        <div className="max-w-7xl mx-auto animate-fade-up">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
                <div>
                    <div className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500">Intake CRM</div>
                    <h1 className="font-display text-4xl font-extrabold tracking-tight">Pipeline</h1>
                    <p className="text-sm text-slate-500 mt-2">Drag leads through Leads → Consultation → Retained.</p>
                </div>
                <button onClick={() => setOpen(true)} data-testid="new-lead-button" className="btn-primary inline-flex items-center gap-2">
                    <Plus size={14} weight="bold" /> New lead
                </button>
            </div>

            <div className="grid md:grid-cols-3 gap-5">
                {STAGES.map((stage) => {
                    const items = leads.filter((l) => l.stage === stage);
                    const meta = STAGE_META[stage];
                    return (
                        <div key={stage} className={`bg-slate-50 border border-slate-200 border-t-2 ${meta.color} p-4 min-h-[500px] rounded-sm`} data-testid={`stage-${stage.toLowerCase()}`}>
                            <div className="flex items-center justify-between mb-4">
                                <div className="font-display font-bold text-slate-900">{stage}</div>
                                <span className={`badge ${meta.chip} border-transparent`}>{items.length}</span>
                            </div>
                            <div className="space-y-3">
                                {items.map((l) => (
                                    <div key={l.lead_id} className="card-sharp p-4 hover:border-[#0B2447] transition-colors group" data-testid={`lead-${l.lead_id}`}>
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="min-w-0">
                                                <div className="font-semibold text-sm truncate">{l.name}</div>
                                                <div className="text-xs text-slate-500 truncate">{l.email}</div>
                                            </div>
                                            <button onClick={() => remove(l.lead_id)} className="text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition" data-testid={`delete-lead-${l.lead_id}`}>
                                                <X size={14} />
                                            </button>
                                        </div>
                                        {l.case_type && <div className="mt-2 text-[10px] font-mono uppercase tracking-wider text-slate-500">{l.case_type}</div>}
                                        {l.matter_description && <div className="text-xs text-slate-600 mt-2 line-clamp-2">{l.matter_description}</div>}
                                        <div className="text-[10px] font-mono text-slate-400 mt-3">{l.source} · {fmtDate(l.created_at)}</div>
                                        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-100">
                                            {stage !== "Retained" && (
                                                <button
                                                    onClick={() => {
                                                        const next = STAGES[STAGES.indexOf(stage) + 1];
                                                        moveLead(l.lead_id, next);
                                                    }}
                                                    data-testid={`advance-${l.lead_id}`}
                                                    className="text-xs text-[#0B2447] hover:underline font-semibold inline-flex items-center gap-1"
                                                >
                                                    Move to {STAGES[STAGES.indexOf(stage) + 1]} <ArrowRight size={11} />
                                                </button>
                                            )}
                                            {stage === "Retained" && !l.converted_case_id && (
                                                <button onClick={() => convert(l.lead_id)} className="text-xs text-emerald-700 hover:underline font-semibold" data-testid={`convert-${l.lead_id}`}>
                                                    Convert → Case
                                                </button>
                                            )}
                                            {l.converted_case_id && (
                                                <span className="text-[10px] font-mono uppercase tracking-wider text-emerald-700">✓ Case opened</span>
                                            )}
                                        </div>
                                    </div>
                                ))}
                                {items.length === 0 && (
                                    <div className="text-xs text-slate-400 text-center py-6 border border-dashed border-slate-200 rounded-sm">
                                        Drop a lead here.
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {open && <NewLeadModal onClose={() => setOpen(false)} onCreated={() => { setOpen(false); load(); toast.success("Lead added"); }} />}
        </div>
    );
}

function NewLeadModal({ onClose, onCreated }) {
    const [form, setForm] = useState({ name: "", email: "", phone: "", case_type: "Personal Injury", matter_description: "", source: "Direct" });
    const submit = async (e) => {
        e.preventDefault();
        try { await api.post("/leads", form); onCreated(); }
        catch { toast.error("Failed"); }
    };
    return (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <form onSubmit={submit} className="bg-white max-w-lg w-full p-7 rounded-sm border border-slate-200 shadow-xl animate-fade-up" data-testid="new-lead-modal">
                <div className="flex items-center justify-between mb-5">
                    <h3 className="font-display text-2xl font-bold">Add lead</h3>
                    <button type="button" onClick={onClose}><X size={18} /></button>
                </div>
                <div className="space-y-4">
                    <div><label className="label-sharp">Name</label><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-sharp" data-testid="lead-name-input" /></div>
                    <div className="grid grid-cols-2 gap-3">
                        <div><label className="label-sharp">Email</label><input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input-sharp" /></div>
                        <div><label className="label-sharp">Phone</label><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="input-sharp" /></div>
                    </div>
                    <div>
                        <label className="label-sharp">Matter type</label>
                        <select value={form.case_type} onChange={(e) => setForm({ ...form, case_type: e.target.value })} className="input-sharp">
                            {CASE_TYPES.map((t) => <option key={t}>{t}</option>)}
                        </select>
                    </div>
                    <div><label className="label-sharp">Description</label><textarea rows={3} value={form.matter_description} onChange={(e) => setForm({ ...form, matter_description: e.target.value })} className="input-sharp" /></div>
                </div>
                <div className="flex justify-end gap-2 mt-6">
                    <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
                    <button type="submit" className="btn-primary" data-testid="lead-submit-button">Add lead</button>
                </div>
            </form>
        </div>
    );
}
